<script setup lang="ts">
import type { Schemas } from '#shopware'

const { t } = useI18n()
const localePath = useLocalePath()
const route = useRoute()

const filter = computed(() => route.params.filter as string)

const navigationItems = inject<Ref<Schemas['NavigationRouteResponse'] | null>>('mainNavigation', ref(null))

const mainCategories = computed<Schemas['Category'][]>(() =>
    (navigationItems.value as Schemas['Category'][] | null) ?? []
)

const markenCategory = computed(() =>
    mainCategories.value.find(cat => cat.name === 'Marken')
)

const allManufacturers = computed<Schemas['Category'][]>(() =>
    ((markenCategory.value?.children as Schemas['Category'][]) ?? []).filter(cat => cat.active)
)

const FILTER_FIELD_MAP: Record<string, string> = {
    herrenschuhe: 'demographic_men_category_id',
    damenschuhe: 'demographic_women_category_id',
    kinderschuhe: 'demographic_children_category_id',
}

const manufacturers = computed<Schemas['Category'][]>(() => {
    const fieldName = FILTER_FIELD_MAP[filter.value.toLowerCase()]
    if (!fieldName) return []
    return allManufacturers.value.filter(mfg => {
        const cf = mfg.customFields as Record<string, unknown> | undefined
        const categoryId = cf?.[fieldName] as string | undefined
        if (!categoryId) return false
        const children = (mfg.children as Schemas['Category'][]) ?? []
        const child = children.find(c => c.id === categoryId)
        return !!child?.active
    })
})

useSeoMeta({
    title: t(`marken.filterPage.${filter.value}.title`),
    description: t('marken.meta.description'),
    robots: 'INDEX, FOLLOW'
})
</script>

<template>
    <div>
        <MarkenSubNavigation :active-filter="filter" />

        <div class="mx-auto max-w-8xl px-4 py-8">
            <h1 class="hidden md:block text-3xl font-bold mb-6">
                {{ $t('marken.pageTitle') }}
            </h1>

            <div class="flex flex-wrap flex-col md:flex-row gap-3 mb-8">
                <NuxtLink
                    :to="localePath('/marken')"
                    class="btn btn-medium btn-secondary"
                >
                    {{ $t('marken.filter.all') }}
                </NuxtLink>
                <NuxtLink
                    :to="localePath('/marken/damenschuhe')"
                    :class="['btn btn-medium', filter === 'damenschuhe' ? 'btn-primary' : 'btn-secondary']"
                >
                    {{ $t('marken.filter.women') }}
                </NuxtLink>
                <NuxtLink
                    :to="localePath('/marken/herrenschuhe')"
                    :class="['btn btn-medium', filter === 'herrenschuhe' ? 'btn-primary' : 'btn-secondary']"
                >
                    {{ $t('marken.filter.men') }}
                </NuxtLink>
                <NuxtLink
                    :to="localePath('/marken/kinderschuhe')"
                    :class="['btn btn-medium', filter === 'kinderschuhe' ? 'btn-primary' : 'btn-secondary']"
                >
                    {{ $t('marken.filter.kids') }}
                </NuxtLink>
            </div>

            <MarkenLexicon :manufacturers="manufacturers" :active-filter="filter" />
        </div>
    </div>
</template>